import React, { forwardRef } from 'react';
import { styled } from '@mui/material';

import Icon from 'shell/components/Icon';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import Tooltip from 'shell/components/Tooltip';
import { getTooltipProps } from 'utils/helper';

const TYPES = {
    general: ['new'],
    invitation: ['pending', 'declined', 'expired'],
    process: ['inProgress'],
    progress: ['onTrack', 'caution', 'offTrack'],
    role: ['instructor'],
    status: ['toDo', 'inProgress', 'underReview'],
    submission: ['submitted', 'late'],
    task: ['milestone'],
    workload: ['low', 'medium', 'high'],
};

const CustomStack = styled(Stack)(({ size, type, value, theme }) => {
    const padding = {
        small: theme.spacing(0, 1),
        large: theme.spacing(1, 1.5),
    }[size];

    return {
        borderRadius: '4px',
        padding,
        ...(size === 'small' && {
            height: '1rem',
            ...(value === 'instructor' && {
                display: 'inline-flex',
                background: theme.palette.ensightful.neutral[800],
                color: theme.palette.ensightful.shades[0],
            }),
        }),
        ...(size === 'large' && {
            minWidth: '4.25rem',
            height: '1.5rem',
            ...(type === 'general' && {
                ...(value === 'new' && {
                    background: theme.palette.ensightful.success[50],
                    color: theme.palette.ensightful.success[800],
                }),
            }),
            ...(type === 'invitation' && {
                ...(value === 'pending' && {
                    background: theme.palette.ensightful.neutral[100],
                    color: theme.palette.ensightful.neutral[800],
                }),
                ...(value === 'declined' && {
                    background: theme.palette.ensightful.error[100],
                    color: theme.palette.ensightful.error[900],
                }),
                ...(value === 'expired' && {
                    background: theme.palette.ensightful.neutral[100],
                    color: theme.palette.ensightful.neutral[800],
                }),
            }),
            ...(type === 'process' && {
                ...(value === 'inProgress' && {
                    background: theme.palette.ensightful.success[50],
                    color: theme.palette.ensightful.success[800],
                }),
            }),
            ...(type === 'progress' && {
                ...(value === 'onTrack' && {
                    background: theme.palette.ensightful.success[50],
                    color: theme.palette.ensightful.success[800],
                }),
                ...(value === 'caution' && {
                    background: theme.palette.ensightful.warning[100],
                    color: theme.palette.ensightful.warning[900],
                }),
                ...(value === 'offTrack' && {
                    background: theme.palette.ensightful.error[100],
                    color: theme.palette.ensightful.error[900],
                }),
            }),
            ...(type === 'task' && {
                ...(value === 'milestone' && {
                    borderRadius: '12px',
                    background: theme.palette.ensightful.primary[700],
                    color: theme.palette.ensightful.primary[50],
                }),
            }),
            ...(type === 'status' && {
                minWidth: 'unset',
                borderRadius: '12px',
                background: theme.palette.ensightful.neutral[800],
                color: theme.palette.ensightful.neutral[50],
            }),
            ...(type === 'submission' && {
                ...(value === 'submitted' && {
                    background: theme.palette.ensightful.success[50],
                    color: theme.palette.ensightful.success[800],
                }),
                ...(value === 'late' && {
                    background: theme.palette.ensightful.error[100],
                    color: theme.palette.ensightful.error[900],
                }),
            }),
            ...(type === 'workload' && {
                borderRadius: '12px',
                ...(value === 'low' && {
                    border: `1px solid ${theme.palette.ensightful.secondary[600]}`,
                    background: theme.palette.ensightful.secondary[50],
                    color: theme.palette.ensightful.secondary[600],
                }),
                ...(value === 'medium' && {
                    border: `1px solid ${theme.palette.ensightful.warning[900]}`,
                    background: theme.palette.ensightful.warning[100],
                    color: theme.palette.ensightful.warning[900],
                }),
                ...(value === 'high' && {
                    border: `1px solid ${theme.palette.ensightful.error[900]}`,
                    background: theme.palette.ensightful.error[100],
                    color: theme.palette.ensightful.error[900],
                }),
            }),
        }),
    };
});

const CustomIcon = styled(Icon)(({ kind, theme }) => {
    return {
        fontSize: '0.625rem',
        ...(kind === 'starFilled' && {
            color: theme.palette.ensightful.primary[50],
        }),
    };
});

const CustomText = styled(Text)(() => {
    return {
        marginTop: '1px',
    };
});

const BaseTag = forwardRef(({ component, size, type, value, ...rest }, ref) => {
    const icon = {
        milestone: <CustomIcon kind={'starFilled'} />,
        submitted: <CustomIcon kind={'checkmark'} />,
    }[value];

    const text = {
        // general
        new: 'New',
        // invitation
        pending: 'Pending',
        declined: 'Declined',
        expired: 'Invite Expired',
        // process
        inProgress: 'In Progress',
        // progress
        onTrack: 'On Track',
        caution: 'Caution',
        offTrack: 'Off Track',
        // role
        instructor: 'Instructor',
        // status
        toDo: 'To Do',
        inProgress: 'In Progress',
        underReview: 'Under Review',
        // submission
        submitted: 'Submitted',
        late: 'Late',
        // task
        milestone: 'Milestone',
        // workload
        low: 'Low',
        medium: 'Medium',
        high: 'High',
    }[value];

    return (
        <CustomStack
            ref={ref}
            alignItems={'center'}
            component={component}
            direction={'row'}
            justifyContent={'space-around'}
            size={size}
            spacing={1}
            type={type}
            value={value}
            {...rest}
        >
            {icon}
            <CustomText align={'center'} color={'inherit'} component={component || 'div'} variant={'p3'}>
                {text}
            </CustomText>
        </CustomStack>
    );
});

export default forwardRef(({ tooltip, type, value, ...rest }, ref) => {
    if (tooltip && !tooltip.component)
        throw new Error(
            `Shell: Invalid tooltip in component Tag. Expected "tooltip.component" to be "string" or "ReactElement", but got "${typeof tooltip.component}" instead.`
        );
    if (!Object.keys(TYPES).includes(type)) throw new Error(`Shell: Invalid type ${type} in component Tag.`);
    if (!TYPES[type].includes(value)) throw new Error(`Shell: Invalid value ${value} in component Tag.`);

    const TagComponent = (
        <BaseTag ref={ref} size={type === 'role' ? 'small' : 'large'} type={type} value={value} {...rest} />
    );

    return tooltip ? <Tooltip {...getTooltipProps(tooltip)}>{TagComponent}</Tooltip> : TagComponent;
});
